import { applyNodeChanges, type NodeChange } from '@xyflow/react';
import { produce } from 'immer';

import { SELECTABLE_NODE_TYPES } from '../../constants';
import { applyTextNodeSnapshot } from '../../nodes/Text/applyTextNodeSnapshot';
import { useEditCanvasStore } from '../../store';
import { type AppNode } from '../../types';
import { getGroupMembers } from '../../utils/group/nodeGrouping';
import { type NodeZIndex } from '../../utils/nodeLayering';
import { type PasteGroup } from '../clipboard/utils';
import { type GroupMutations } from '../sync/useCanvasSync';
import { type Command, type NodeDataUpdate, type NodePosition } from './types';

type DissolvedGroupInfo = {
  groupId: string;
  memberRemoteIds: string[];
  memberLocalIds: string[];
};

/**
 * Detects groups that will be auto-dissolved by the backend when the given
 * nodes are deleted. A group auto-dissolves when deleting members leaves it
 * with ≤1 remaining children.
 */
function detectAutoDissolvedGroups(allNodes: AppNode[], deletedNodes: AppNode[]): DissolvedGroupInfo[] {
  const deletedIds = new Set(deletedNodes.map((n) => n.id));
  const affectedGroupIds = new Set<string>();

  for (const node of deletedNodes) {
    if (node.groupId) {
      affectedGroupIds.add(node.groupId);
    }
  }

  const dissolved: DissolvedGroupInfo[] = [];

  for (const groupId of affectedGroupIds) {
    const members = getGroupMembers(allNodes, groupId);
    const remaining = members.filter((m) => !deletedIds.has(m.id));

    if (remaining.length <= 1) {
      dissolved.push({
        groupId,
        memberLocalIds: members.map((m) => m.id),
        memberRemoteIds: members.map((m) => m.remoteId).filter((id): id is string => !!id),
      });
    }
  }

  return dissolved;
}

/**
 * Replaces a node's `.data` entirely (not merge).
 * Removes keys absent from `newData` so that properties which didn't exist
 * in the original snapshot are properly cleaned up on undo.
 */
function replaceNodeData(nodeId: string, newData: AppNode['data']) {
  useEditCanvasStore.setState((state) => ({
    nodes: state.nodes.map((n) => {
      if (n.id !== nodeId) {
        return n;
      }

      return produce(n, (draft) => {
        for (const key of Object.keys(draft.data)) {
          if (!(key in newData)) {
            Reflect.deleteProperty(draft.data, key);
          }
        }

        Object.assign(draft.data, newData);
      });
    }),
  }));
}

export type AddNodesOptions = {
  zoomToFit?: boolean;
  zoomToFitIfFirstNode?: boolean;
  unselectExistingNodes?: boolean;
  selectNewNodes?: boolean;
  /** Node IDs to remove in the same state update (e.g. preview placeholders). Not synced to server. */
  removeNodeIds?: string[];
};

const DEFAULT_ADD_NODES_OPTIONS: Required<AddNodesOptions> = {
  removeNodeIds: [],
  selectNewNodes: true,
  unselectExistingNodes: true,
  zoomToFit: true,
  zoomToFitIfFirstNode: false,
};

/**
 * After pasted/duplicated nodes are synced to the server, creates groups
 * for nodes that were grouped in the source. Fire-and-forget — undo is
 * handled by deleting nodes, which auto-dissolves groups on the backend.
 */
export function createGroupsAfterPaste(groups: string[][]): void {
  if (groups.length === 0) {
    return;
  }

  const { flushSync, groupMutations } = useEditCanvasStore.getState();

  if (!groupMutations || !flushSync) {
    // Can't create server groups — clear optimistic temp groupIds
    for (const localIds of groups) {
      useEditCanvasStore.getState().setGroupId(localIds, undefined);
    }

    return;
  }

  flushSync()
    .then(async () => {
      const storeNodes = useEditCanvasStore.getState().nodes;

      for (const localIds of groups) {
        const remoteIds = localIds
          .map((id) => storeNodes.find((n) => n.id === id)?.remoteId)
          .filter((id): id is string => !!id);

        if (remoteIds.length < 2) {
          useEditCanvasStore.getState().setGroupId(localIds, undefined);
          continue;
        }

        try {
          const response = await groupMutations.createGroup({ nodeIds: remoteIds });
          useEditCanvasStore.getState().setGroupId(localIds, response.groupNode.id);
        } catch {
          // Clear the optimistic temp groupId so the UI doesn't show a
          // group that the server doesn't know about.
          useEditCanvasStore.getState().setGroupId(localIds, undefined);
        }
      }
    })
    .catch(() => {
      for (const localIds of groups) {
        useEditCanvasStore.getState().setGroupId(localIds, undefined);
      }
    });
}

export const commands = {
  addNodes: (nodes: AppNode[], options: AddNodesOptions = DEFAULT_ADD_NODES_OPTIONS): Command => {
    const mergedOptions = { ...DEFAULT_ADD_NODES_OPTIONS, ...options };

    const previousSelectedNodeIds = new Set(
      useEditCanvasStore
        .getState()
        .nodes.filter((n) => n.selected)
        .map((n) => n.id),
    );

    return {
      execute: () => {
        const canvasHasNodes = useEditCanvasStore
          .getState()
          .nodes.some((n) => n.type && SELECTABLE_NODE_TYPES.has(n.type));
        const selectNew = mergedOptions.selectNewNodes;

        // Pre-set selected on new nodes — ReactFlow must see selection on the
        // first store notification for drag handling to initialize correctly.
        // Deselect existing nodes in the same atomic update.
        useEditCanvasStore.getState().addNodes(
          nodes.map((n) => ({ ...n, selected: selectNew })),
          {
            removeNodeIds: mergedOptions.removeNodeIds,
            unselectExistingNodes: mergedOptions.unselectExistingNodes,
          },
        );

        const shouldZoomToFit = mergedOptions.zoomToFit || (!canvasHasNodes && mergedOptions.zoomToFitIfFirstNode);

        if (shouldZoomToFit) {
          const zoomToFit = useEditCanvasStore.getState().zoomToFit;

          if (zoomToFit) {
            const newNodeIds = new Set(nodes.map((n) => n.id));
            zoomToFit({ nodeIds: Array.from(newNodeIds) });
          }
        }
      },
      undo: () => {
        useEditCanvasStore.getState().removeNodes(nodes.map((n) => n.id));
        useEditCanvasStore.setState((state) => {
          const changes: NodeChange<AppNode>[] = state.nodes.map((n) => ({
            id: n.id,
            selected: previousSelectedNodeIds.has(n.id),
            type: 'select',
          }));

          return { nodes: applyNodeChanges(changes, state.nodes) };
        });
      },
    };
  },

  groupNodes: (
    nodeIds: string[],
    groupId: string,
    remoteNodeIds: string[],
    api: GroupMutations,
    zIndexUpdates?: { previous: NodeZIndex[]; next: NodeZIndex[] },
  ): Command => ({
    execute: () => {
      useEditCanvasStore.getState().setGroupId(nodeIds, groupId);

      if (zIndexUpdates) {
        useEditCanvasStore.getState().updateZIndexes(zIndexUpdates.next);
      }

      // Must not throw — errors here corrupt the undo stack; server reconciles on next canvas load
      api.reactivateGroup({ groupNodeId: groupId, nodeIds: remoteNodeIds }).catch(() => {});
    },
    undo: () => {
      useEditCanvasStore.getState().setGroupId(nodeIds, undefined);

      if (zIndexUpdates) {
        useEditCanvasStore.getState().updateZIndexes(zIndexUpdates.previous);
      }

      // Must not throw — errors here corrupt the undo stack; server reconciles on next canvas load
      api.deleteGroup({ groupNodeId: groupId }).catch(() => {});
    },
  }),

  modifyNodes: (previousSnapshots: AppNode[], newSnapshots: AppNode[]): Command => ({
    execute: () => {
      useEditCanvasStore.getState().moveNodes(newSnapshots.map((n) => ({ id: n.id, position: n.position })));
      useEditCanvasStore.getState().resizeNodes(
        newSnapshots.flatMap((n) => {
          const { width, height } = n.measured ?? {};

          return typeof width === 'number' && typeof height === 'number' ? [{ height, id: n.id, width }] : [];
        }),
      );
    },
    undo: () => {
      useEditCanvasStore.getState().moveNodes(previousSnapshots.map((n) => ({ id: n.id, position: n.position })));
      useEditCanvasStore.getState().resizeNodes(
        previousSnapshots.flatMap((n) => {
          const { width, height } = n.measured ?? {};

          return typeof width === 'number' && typeof height === 'number' ? [{ height, id: n.id, width }] : [];
        }),
      );
    },
  }),

  /**
   * Combined text node resize command that captures position, dimensions, AND data
   * (fontSize, baseWidth, etc.) as a single undoable operation.
   */
  modifyTextNode: (previous: AppNode, current: AppNode): Command => ({
    execute: () => applyTextNodeSnapshot(current),
    undo: () => applyTextNodeSnapshot(previous),
  }),

  moveNodes: (previousPositions: NodePosition[], newPositions: NodePosition[]): Command => ({
    execute: () => useEditCanvasStore.getState().moveNodes(newPositions),
    undo: () => useEditCanvasStore.getState().moveNodes(previousPositions),
  }),

  /**
   * Paste/duplicate command that generates fresh node IDs on each execute (including redo)
   * and recreates groups on the backend. Undo removes nodes; backend auto-dissolves groups.
   */
  pasteNodes: (templateNodes: AppNode[], groups: PasteGroup[]): Command => {
    // Snapshot once at command creation (same moment as the first execute, before
    // nodes are added). Redo reuses this command — do not re-read selection on
    // execute or undo would restore whatever was selected before redo, not the
    // original pre-paste selection.
    const previousSelectedNodeIds = new Set(
      useEditCanvasStore
        .getState()
        .nodes.filter((n) => n.selected)
        .map((n) => n.id),
    );

    let currentNodeIds: string[] = [];

    return {
      execute: () => {
        // Generate fresh IDs each time (initial paste + redo)
        const freshNodes = templateNodes.map((n) => ({
          ...n,
          id: crypto.randomUUID(),
          remoteId: undefined,
          selected: true,
        }));
        currentNodeIds = freshNodes.map((n) => n.id);

        useEditCanvasStore.getState().addNodes(freshNodes, { unselectExistingNodes: true });

        if (groups.length > 0) {
          const groupLocalIds: string[][] = [];

          for (const group of groups) {
            const memberIds = group.memberIndices.map((idx) => freshNodes[idx].id);
            groupLocalIds.push(memberIds);
            useEditCanvasStore.getState().setGroupId(memberIds, crypto.randomUUID());
          }

          createGroupsAfterPaste(groupLocalIds);
        }
      },
      undo: () => {
        useEditCanvasStore.getState().removeNodes(currentNodeIds);
        useEditCanvasStore.setState((state) => {
          const changes: NodeChange<AppNode>[] = state.nodes.map((n) => ({
            id: n.id,
            selected: previousSelectedNodeIds.has(n.id),
            type: 'select',
          }));

          return { nodes: applyNodeChanges(changes, state.nodes) };
        });
      },
    };
  },

  removeNodes: (nodes: AppNode[]): Command => {
    const allNodes = useEditCanvasStore.getState().nodes;
    const dissolvedGroups = detectAutoDissolvedGroups(allNodes, nodes);

    return {
      execute: () => {
        useEditCanvasStore.getState().removeNodes(nodes.map((n) => n.id));
      },
      undo: () => {
        useEditCanvasStore.getState().addNodes(nodes);

        // Reactivate groups that were auto-dissolved by the backend.
        // Must flush the sync queue first so the re-added nodes are
        // reactivated on the server before we reactivate the group
        // (the server rejects group reactivation if children aren't active).
        if (dissolvedGroups.length > 0) {
          const { groupMutations, flushSync } = useEditCanvasStore.getState();

          if (groupMutations) {
            for (const { groupId, memberLocalIds } of dissolvedGroups) {
              useEditCanvasStore.getState().setGroupId(memberLocalIds, groupId);
            }

            const flush = flushSync?.() ?? Promise.resolve();
            flush
              .then(() => {
                for (const { groupId, memberRemoteIds } of dissolvedGroups) {
                  groupMutations.reactivateGroup({ groupNodeId: groupId, nodeIds: memberRemoteIds }).catch(() => {});
                }
              })
              .catch(() => {});
          }
        }
      },
    };
  },

  /**
   * Generic command for undoing/redoing a node's data changes with optional server sync.
   * Used by the node data snapshot system when a node is deselected.
   */
  restoreNodeData: (
    nodeId: string,
    previousData: AppNode['data'],
    newData: AppNode['data'],
    syncFn?: (nodeId: string) => void,
  ): Command => ({
    execute: () => {
      replaceNodeData(nodeId, newData);
      syncFn?.(nodeId);
    },
    undo: () => {
      replaceNodeData(nodeId, previousData);
      syncFn?.(nodeId);
    },
  }),

  ungroupNodes: (nodeIds: string[], groupId: string, remoteNodeIds: string[], api: GroupMutations): Command => ({
    execute: () => {
      useEditCanvasStore.getState().setGroupId(nodeIds, undefined);

      // Must not throw — errors here corrupt the undo stack; server reconciles on next canvas load
      api.deleteGroup({ groupNodeId: groupId }).catch(() => {});
    },
    undo: () => {
      useEditCanvasStore.getState().setGroupId(nodeIds, groupId);

      // Must not throw — errors here corrupt the undo stack; server reconciles on next canvas load
      api.reactivateGroup({ groupNodeId: groupId, nodeIds: remoteNodeIds }).catch(() => {});
    },
  }),

  updateNodesData: (updates: NodeDataUpdate[]): Command => ({
    execute: () => {
      for (const { nodeId, newData } of updates) {
        useEditCanvasStore.getState().updateNodeData(nodeId, newData);
      }
    },
    undo: () => {
      for (const { nodeId, previousData } of updates) {
        useEditCanvasStore.getState().updateNodeData(nodeId, previousData);
      }
    },
  }),

  updateZIndexes: (previousZIndexes: NodeZIndex[], newZIndexes: NodeZIndex[]): Command => ({
    execute: () => {
      useEditCanvasStore.getState().updateZIndexes(newZIndexes);
    },
    undo: () => {
      useEditCanvasStore.getState().updateZIndexes(previousZIndexes);
    },
  }),
};
